// src/lib/analyticsV2Format.ts
//
// Value-shape helpers for the v2 read API's `MetricResultDto.value`
// (`Domain/ValueObjects/V2MetricResult.cs`): a `decimal`/`int`/`long`
// count or share, a `TimeSpan` duration (serialised as .NET's default
// `[-][d.]hh:mm:ss[.fffffff]` string), or a `Dictionary<string, object>`
// breakdown row (`02` S1's *Counts* rows). Same DASH-for-null rule as
// `analyticsFormat.ts` (Decision-11) — nothing here ever coerces an absent
// cell to zero.
import { DASH, formatCount, formatPct, formatSeconds, formatSignedDelta, type FormattedDelta } from './analyticsFormat';
import type { MetricValue } from '@/types/analyticsV2';

const DURATION_PATTERN = /^(-)?(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/;

/** Parses a .NET `TimeSpan` wire string into total seconds; `null` if the
 *  value isn't one (a plain number is returned as-is — already seconds). */
export const parseV2DurationSeconds = (value: unknown): number | null => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;
  const m = value.match(DURATION_PATTERN);
  if (!m) return null;
  const [, neg, days, hours, minutes, seconds, frac] = m;
  const total =
    (Number(days) || 0) * 86400 + Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds) + (frac ? Number(`0.${frac}`) : 0);
  return neg ? -total : total;
};

export const formatV2Duration = (value: unknown): string => {
  const seconds = parseV2DurationSeconds(value);
  return seconds === null ? DASH : formatSeconds(seconds);
};

/** Some engines (`chain.median`, `fin.reconciliation_rate`...) state a 0–1
 *  ratio; a value already above 1 is assumed to arrive as a 0–100 percentage
 *  instead — defensive, not verified against a live payload (no Postgres in
 *  this environment, the same gap every prior unit in this run recorded). */
export const formatV2Share = (value: unknown): string => {
  if (typeof value !== 'number') return DASH;
  return formatPct(value <= 1 ? value * 100 : value);
};

/** Percentage-point delta for a share card (the raw `delta` is the same 0–1
 *  fraction the value is, so it is scaled the same way before display). */
export const formatV2ShareDelta = (delta: number): FormattedDelta =>
  formatSignedDelta(delta <= 1 && delta >= -1 ? delta * 100 : delta, 1, ' pp');

export const formatV2CountDelta = (delta: number): FormattedDelta => formatSignedDelta(delta, 0);

/** Exact ids whose wire value is a 0-1 ratio (`V2MetricEngine`'s `Rate()`
 *  helper, `StallShare`, `it.availability`/`it.unknown_share`) — an
 *  id-substring guess ("contains 'share'") is not reliable here:
 *  `fit.language_share`'s own dictionary values are plain session counts
 *  (`V2FitMetricEngine.LanguageShare`: `g => g.Count()`), not fractions,
 *  despite the name. Read from the engines directly rather than guessed. */
const SHARE_IDS = new Set([
  'fin.reconciliation_rate',
  'it.availability',
  'it.unknown_share',
  'fit.completion_rate',
  'fit.method_fallback',
  'fit.backtrack_share',
  'fit.stall_share',
]);
/** The Operación page's own rule for its `ops.*` ids (`ops.containment`,
 *  `ops.pre_checkin_share`...): a share by name. Other families use the
 *  exact id set above, where a name alone is not reliable. */
const isOpsShareMetric = (id: string): boolean => id.startsWith('ops.') && /rate|share|containment|fallback/i.test(id);
export const isShareMetric = (id: string): boolean => SHARE_IDS.has(id) || isOpsShareMetric(id);

/** A scalar cell is a duration exactly when its wire value is a `TimeSpan`
 *  string (`parseV2DurationSeconds` already validates the shape) — never a
 *  plain number, which every count/share cell also is. */
export const isV2DurationString = (value: unknown): boolean => typeof value === 'string' && parseV2DurationSeconds(value) !== null;


export const formatV2Cell = (id: string, value: unknown): string => {
  if (isV2DurationString(value)) return formatV2Duration(value);
  if (isShareMetric(id)) return formatV2Share(value);
  if (typeof value === 'number') return formatCount(value);
  if (typeof value === 'string') return value;
  return DASH;
};

/** Live elapsed-time counter for the board's own `elapsed_seconds` /
 *  `completed_seconds_ago` row fields — always a plain number of seconds
 *  already, unlike `formatV2Duration`'s TimeSpan strings, and rendered as
 *  `mm:ss` up to an hour (a journey stuck for over an hour states its hours
 *  too) rather than the "2 min 45 s" prose form used elsewhere, since this
 *  one ticks live on screen and needs to stay compact. */
export const formatElapsedSeconds = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

/** Change of a duration card (the API states it in seconds): «+30 s», «-1 min 5 s». */
export const formatV2DurationDelta = (deltaSeconds: number): FormattedDelta => {
  const rounded = Math.round(Math.abs(deltaSeconds));
  const trend = rounded === 0 ? 'flat' : deltaSeconds > 0 ? 'up' : 'down';
  const sign = trend === 'up' ? '+' : trend === 'down' ? '-' : '';
  return { text: `${sign}${formatSeconds(rounded)}`, trend };
};
